import MonacoEditor, { type BeforeMount, type Monaco as MonacoType, type OnMount } from '@monaco-editor/react';
import { Box, Skeleton } from '@mui/material';
import type { WidgetProps } from 'app/schema-form/types';
import type { editor } from 'monaco-editor';
import type React from 'react';
import type { MutableRefObject } from 'react';
import type { UseResizeDetectorReturn } from 'react-resize-detector';
import styled from 'styled-components';
import Widget from '../widget.js';
import type { MonacoProps } from './monaco-hook.js';

type ResizeDetectorRef = UseResizeDetectorReturn<HTMLDivElement>['ref'];

export type MonacoOwnProps = WidgetProps & {
  readonly monacoLanguage?: string;
  readonly editorInstanceRef?: MutableRefObject<editor.IStandaloneCodeEditor | undefined>;
  readonly monacoInstanceRef?: MutableRefObject<MonacoType | undefined>;
  readonly resizable?: boolean;
};

export type MonacoContainerProps = {
  readonly resizeDetectorRef?: ResizeDetectorRef;
  readonly monacoHeight: string;
  readonly handleChange: (value?: string) => void;
  readonly handleEditorWillMount: BeforeMount;
  readonly handleEditorDidMount: OnMount;
};

const StyledWidget = styled(Widget)`
  height: 100%;
`;

const HEIGHT = 110;

export const Monaco: React.FC<MonacoProps> = (props) => {
  const {
    testId,
    resizable = false,
    resizeDetectorRef,
    monacoHeight,
    monacoLanguage = 'json',
    handleEditorWillMount,
    handleEditorDidMount,
    handleChange,
    isDragging,
    ...widgetProps
  } = props;

  return (
    <Box
      ref={resizeDetectorRef}
      data-testid={testId}
      sx={{
        height: '100%',
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <StyledWidget {...widgetProps}>
        <Box
          sx={{
            resize: resizable ? 'vertical' : undefined,
            overflow: 'hidden',
            height: HEIGHT,
            display: isDragging ? 'none' : undefined
          }}
        >
          <MonacoEditor
            width="100%"
            height={monacoHeight}
            language={monacoLanguage}
            theme="vs-dark"
            line={1}
            loading="Loading..."
            onChange={handleChange}
            beforeMount={handleEditorWillMount}
            onMount={handleEditorDidMount}
            options={{
              tabSize: 2,
              minimap: {
                enabled: false
              },
              fixedOverflowWidgets: true,
              automaticLayout: true
            }}
          />
        </Box>
        {isDragging && (
          <Skeleton
            animation={false}
            variant="rectangular"
            height={HEIGHT}
            sx={{ p: 0, m: 0, bgcolor: 'common.black' }}
          />
        )}
      </StyledWidget>
    </Box>
  );
};
